import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL + "/v1";

// ============================================
// Member API Instance
// ============================================
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Member request interceptor - include member auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Member response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 423 (Password change required), let the calling code handle it
    if (error.response && error.response.status === 423) {
      const response = {
        status: 423,
        data: error.response.data || { message: "Password change required" },
        headers: error.response.headers,
        config: error.config,
      };
      return Promise.resolve(response);
    }

    // If error is 401 and original request is to /member/me, clear RouteGuard cache
    if (
      error.response &&
      error.response.status === 401 &&
      error.config.url &&
      error.config.url.includes("/member/me")
    ) {
      if (typeof window !== "undefined" && window.routeGuardClearCache) {
        window.routeGuardClearCache();
      }
    }

    // If error is 401 and not already retrying
    const isRefreshTokenRequest = originalRequest.url && originalRequest.url.includes('/member/refresh-token');
    if (error.response && error.response.status === 401 && !originalRequest._retry && !isRefreshTokenRequest) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        const response = await api.post(`/member/refresh-token`, {
          refreshToken: refreshToken,
        });

        const { token } = response.data;
        localStorage.setItem("token", token);
        originalRequest.headers["Authorization"] = `Bearer ${token}`;

        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");

        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// ============================================
// Admin API Instance
// ============================================
const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Admin request interceptor - include admin auth token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Admin response interceptor - handle 401 errors
adminApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Handle 401 - redirect to admin login
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminRefreshToken");
      localStorage.removeItem("adminUser");

      if (!window.location.pathname.includes("/admin/login")) {
        window.location.href = "/admin/login";
      }
    }

    return Promise.reject(error);
  },
);

// Auth services
const authService = {
  login: async (username, password) => {
    try {
      const response = await api.post("/member/login", { username, password });
      // Store tokens and user info
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("refreshToken", response.data.refreshToken);
      localStorage.setItem(
        "user",
        JSON.stringify({ username: response.data.username }),
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  register: async (userData) => {
    return api.post("/member/register", userData);
  },

  logout: async () => {
    try {
      await api.post("/member/logout");
    } finally {
      // Clear local storage regardless of API success
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
    }
  },

  refreshToken: async (refreshToken) => {
    const response = await api.post("/member/refresh-token", { refreshToken });
    return response.data;
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem("user");
    if (userStr) return JSON.parse(userStr);
    return null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem("token");
  },

  changePassword: async (currentPassword, newPassword) => {
    return api.post("/member/change-password", {
      currentPassword,
      newPassword,
    });
  },
};

// Member services
const memberService = {
  getProfile: async () => {
    return api.get("/member/me");
  },

  updateProfile: async (profileData) => {
    return api.put("/member/update", profileData);
  },
};

// Facebook Permission services (for both parent and sub members)
const facebookPermissionService = {
  getPages: async () => {
    return api.get("/facebook-permission/pages");
  },

  publish: async (postData) => {
    return api.post("/facebook-permission/publish", postData);
  },
};

const facebookService = {
  /**
   * Connect a Facebook account to the member's profile
   * @param {Object} fbData - Facebook connection data
   * @param {string} fbData.facebookId - Facebook user ID
   * @param {string} fbData.accessToken - Facebook access token
   * @param {number} fbData.expiresIn - Token expiration time in seconds
   * @param {string} [fbData.refreshToken] - Facebook refresh token (optional)
   * @param {string} [fbData.scope] - Facebook permission scope (optional)
   * @returns {Promise} - Promise with connection response
   */
  connect: (fbData) => {
    return api.post(`/facebook/member/connect`, fbData).then((response) => {
      return response.data;
    });
  },

  /**
   * Disconnect the Facebook account from the member's profile
   * @returns {Promise} - Promise with disconnection response
   */
  disconnect: () => {
    return api.post(`/facebook/member/disconnect`, {}).then((response) => {
      return response.data;
    });
  },

  /**
   * Get the member's Facebook connection details
   * @returns {Promise} - Promise with connection details
   */
  getConnection: () => {
    return api.get(`/facebook/member/connection`).then((response) => {
      return response.data;
    });
  },

  /**
   * Validate if the member's Facebook token is still valid
   * @returns {Promise} - Promise with validation result
   */
  validateToken: () => {
    return api.get(`/facebook/member/validate-token`).then((response) => {
      return response.data;
    });
  },

  /**
   * Get a list of Facebook pages that the member manages
   * @returns {Promise} - Promise with list of Facebook pages
   */
  getPages: () => {
    return api.get(`/facebook/member/pages`).then((response) => {
      return response.data;
    });
  },

  /**
   * Publish content to the member's Facebook timeline
   * @param {Object} postData - Post data
   * @param {string} postData.message - Post message
   * @param {string} [postData.link] - URL to include in the post (optional)
   * @param {string} [postData.picture] - Image URL to include in the post (optional)
   * @returns {Promise} - Promise with publishing result
   */
  publish: (postData) => {
    return api.post(`/facebook/member/publish`, postData).then((response) => {
      return response.data;
    });
  },
};

// Sub-member services
const subMemberService = {
  getSubMembers: async () => {
    return api.get("/submember");
  },

  createSubMember: async (subMemberData) => {
    return api.post("/submember", subMemberData);
  },

  updateSubMember: async (subMemberId, subMemberData) => {
    return api.put(`/submember/${subMemberId}`, subMemberData);
  },

  deleteSubMember: async (subMemberId) => {
    return api.delete(`/submember/${subMemberId}`);
  },

  loginSubMember: async (parentMemberId, username, password) => {
    return api.post("/submember/login", { parentMemberId, username, password });
  },

  isSubMember: () => {
    const user = authService.getCurrentUser();
    return user && user.isSubMember === true;
  },
};

// Permission Management services
const permissionService = {
  getAvailablePermissions: async () => {
    return api.get("/permissions");
  },
};

// Admin Auth services
const adminAuthService = {
  login: async (username, password) => {
    try {
      const response = await adminApi.post("/admin/login", { username, password });
      // Store tokens and admin user info
      localStorage.setItem("adminToken", response.data.token);
      localStorage.setItem("adminRefreshToken", response.data.refreshToken);
      localStorage.setItem(
        "adminUser",
        JSON.stringify({
          username: response.data.username,
          userType: response.data.userType,
          roles: response.data.roles,
        }),
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  logout: async () => {
    try {
      // Optional: Add admin logout endpoint if available
      // await adminApi.post("/admin/logout");
    } finally {
      // Clear admin storage
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminRefreshToken");
      localStorage.removeItem("adminUser");
    }
  },

  getCurrentAdmin: () => {
    const adminStr = localStorage.getItem("adminUser");
    if (adminStr) return JSON.parse(adminStr);
    return null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem("adminToken");
  },

  getToken: () => {
    return localStorage.getItem("adminToken");
  },
};

// Ads Queue services
const adsQueueService = {
  /**
   * Create a new ads queue task for the authenticated member
   * @param {Object} queueData - Queue task data
   * @param {string} queueData.pageId - Facebook page ID
   * @param {string} queueData.taskType - Type of task (live, post, consult)
   * @param {string} queueData.scheduledDate - ISO 8601 date string
   * @param {Object} [queueData.adContent] - Optional ad content details
   * @returns {Promise} - Promise with created queue entry
   */
  createQueueEntry: (queueData) => {
    return api.post(`/member/ads-queue`, queueData).then((response) => response.data);
  },

  /**
   * Get ads queue entries for the authenticated member with pagination and filtering
   * @param {Object} [params] - Optional query parameters
   * @param {number} [params.page] - Page number (default: 1)
   * @param {number} [params.limit] - Items per page (default: 10)
   * @param {string} [params.status] - Filter by status: 'pending', 'wait_assign', 'assigned', 'in_progress', 'completed', 'canceled'
   * @param {string} [params.serviceType] - Filter by service type: 'live', 'post', 'consult'
   * @param {string} [params.sortBy] - Field to sort by (default: 'scheduledDate')
   * @param {string} [params.sortOrder] - Sort order: 'asc' or 'desc' (default: 'desc')
   * @returns {Promise} - Promise with paginated queue entries
   */
  getQueueEntries: (params = {}) => {
    return api.get(`/member/ads-queue`, { params }).then((response) => response.data);
  },
};

// Admin Facebook services
const adminFacebookService = {
  /**
   * Connect Facebook account for admin
   */
  connect: (fbData) => {
    return adminApi.post(`/facebook/admin/connect`, fbData).then((response) => response.data);
  },

  /**
   * Get Facebook pages from admin's connection
   */
  getPages: () => {
    return adminApi.get(`/facebook/admin/pages`).then((response) => response.data);
  },

  /**
   * Get Facebook connection information
   */
  getConnection: () => {
    return adminApi.get(`/facebook/admin/connection`).then((response) => response.data);
  },

  /**
   * Configure a Facebook page for RuaySure integration
   */
  configureRuaysure: (configData) => {
    return adminApi.post(`/facebook/admin/config`, configData).then((response) => response.data);
  },

  /**
   * Get current RuaySure configuration
   */
  getConfig: () => {
    return adminApi.get(`/facebook/admin/config`).then((response) => response.data);
  },

  /**
   * Validate and optionally refresh the RuaySure access token
   */
  validateToken: () => {
    return adminApi.get(`/facebook/admin/validate-token`).then((response) => response.data);
  },

  /**
   * Manually refresh the RuaySure access token
   */
  refreshToken: () => {
    return adminApi.post(`/facebook/admin/refresh-token`, {}).then((response) => response.data);
  },

  /**
   * Disconnect admin's Facebook account
   */
  disconnect: () => {
    return adminApi.post(`/facebook/admin/disconnect`, {}).then((response) => response.data);
  },
};

// Packages services
const packagesService = {
  /**
   * Get all available packages for purchase
   * @returns {Promise} - Promise with list of packages
   */
  getPackages: () => {
    return api.get(`/packages`).then((response) => response.data);
  },

  /**
   * Submit a package purchase order with payment receipt
   * @param {Object} purchaseData - Purchase data
   * @param {number} purchaseData.package_id - ID of the package to purchase
   * @param {File} purchaseData.receipt_image - Payment receipt image file
   * @param {string} [purchaseData.note] - Optional notes (max 500 characters)
   * @returns {Promise} - Promise with purchase confirmation
   */
  submitPurchase: (purchaseData) => {
    const formData = new FormData();
    formData.append('package_id', purchaseData.package_id);
    formData.append('receipt_image', purchaseData.receipt_image);
    if (purchaseData.note) {
      formData.append('note', purchaseData.note);
    }

    return api.post(`/packages/purchase`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((response) => response.data);
  },

  /**
   * Get purchase history for the authenticated member
   * @param {Object} [params] - Optional query parameters
   * @param {string} [params.start_date] - Start date (YYYY-MM-DD)
   * @param {string} [params.end_date] - End date (YYYY-MM-DD)
   * @param {string} [params.status] - Filter by status (pending_verification, active, expired, rejected, all)
   * @param {number} [params.page] - Page number (default: 1)
   * @param {number} [params.limit] - Records per page (default: 20, max: 100)
   * @returns {Promise} - Promise with purchase history
   */
  getPurchaseHistory: (params = {}) => {
    return api.get(`/packages/purchase-history`, { params }).then((response) => response.data);
  },
};

// Help Center services
const helpCenterService = {
  /**
   * Submit a support request
   * @param {Object} requestData - Support request data
   * @param {string} requestData.name - Full name
   * @param {string} requestData.email - Contact email
   * @param {string} requestData.subject - Issue category
   * @param {string} requestData.message - Detailed description
   * @returns {Promise} - Promise with ticket information
   */
  submitSupportRequest: (requestData) => {
    return api.post(`/help-center/member/support-requests`, requestData).then((response) => response.data);
  },

  /**
   * Get user's support requests
   * @param {Object} [params] - Optional query parameters
   * @param {number} [params.page] - Page number
   * @param {number} [params.limit] - Items per page
   * @returns {Promise} - Promise with support tickets
   */
  getSupportRequests: (params = {}) => {
    return api.get(`/help-center/member/support-requests`, { params }).then((response) => response.data);
  },

  /**
   * Get all FAQs (public endpoint)
   * @param {Object} [params] - Optional query parameters
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.language] - Language code (th, en)
   * @returns {Promise} - Promise with FAQs
   */
  getFAQs: (params = {}) => {
    return api.get(`/help-center/public/faqs`, { params }).then((response) => response.data);
  },

  /**
   * Get FAQ by ID (public endpoint)
   * @param {string} faqId - FAQ ID
   * @returns {Promise} - Promise with FAQ details
   */
  getFAQById: (faqId) => {
    return api.get(`/help-center/public/faqs/${faqId}`).then((response) => response.data);
  },

  /**
   * Get all user guides (public endpoint)
   * @param {Object} [params] - Optional query parameters
   * @param {string} [params.language] - Language code (th, en)
   * @returns {Promise} - Promise with user guides
   */
  getUserGuides: (params = {}) => {
    return api.get(`/help-center/public/user-guides`, { params }).then((response) => response.data);
  },

  /**
   * Get all troubleshooting guides (public endpoint)
   * @param {Object} [params] - Optional query parameters
   * @param {string} [params.language] - Language code (th, en)
   * @returns {Promise} - Promise with troubleshooting guides
   */
  getTroubleshootingGuides: (params = {}) => {
    return api.get(`/help-center/public/troubleshooting`, { params }).then((response) => response.data);
  },

  /**
   * Get contact information and support categories (public endpoint)
   * @returns {Promise} - Promise with contact info and categories
   */
  getSettings: () => {
    return api.get(`/help-center/public/settings`).then((response) => response.data);
  },
};

// Ads Status services
const adsStatusService = {
  /**
   * Get system status overview
   * @returns {Promise} - Promise with overall system status
   */
  getOverview: () => {
    return api.get(`/status/overview`).then((response) => response.data);
  },

  /**
   * Get all services status
   * @returns {Promise} - Promise with all services and their status
   */
  getServices: () => {
    return api.get(`/status/services`).then((response) => response.data);
  },

  /**
   * Get service status by ID
   * @param {string} serviceId - Service identifier
   * @returns {Promise} - Promise with service details
   */
  getServiceById: (serviceId) => {
    return api.get(`/status/services/${serviceId}`).then((response) => response.data);
  },

  /**
   * Get recent incidents
   * @param {Object} [params] - Optional query parameters
   * @param {number} [params.limit] - Number of incidents to return
   * @param {string} [params.status] - Filter by status
   * @param {number} [params.days] - Number of days to look back
   * @returns {Promise} - Promise with incidents list
   */
  getIncidents: (params = {}) => {
    return api.get(`/status/incidents`, { params }).then((response) => response.data);
  },

  /**
   * Get incident by ID
   * @param {string} incidentId - Incident identifier
   * @returns {Promise} - Promise with incident details
   */
  getIncidentById: (incidentId) => {
    return api.get(`/status/incidents/${incidentId}`).then((response) => response.data);
  },

  /**
   * Get status history
   * @param {Object} [params] - Optional query parameters
   * @param {string} [params.serviceId] - Filter by service ID
   * @param {string} [params.startDate] - Start date (ISO 8601)
   * @param {string} [params.endDate] - End date (ISO 8601)
   * @param {number} [params.limit] - Number of records
   * @returns {Promise} - Promise with status history
   */
  getHistory: (params = {}) => {
    return api.get(`/status/history`, { params }).then((response) => response.data);
  },

  /**
   * Subscribe to real-time status updates via WebSocket
   * @param {Function} onMessage - Callback for incoming messages
   * @param {Function} onError - Callback for errors
   * @returns {WebSocket} - WebSocket connection
   */
  subscribeToUpdates: (onMessage, onError) => {
    const wsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:8080';
    const token = localStorage.getItem('token');
    const ws = new WebSocket(`${wsUrl}/api/v1/status/subscribe?token=${token}`);
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };
    
    ws.onerror = (error) => {
      if (onError) onError(error);
    };
    
    return ws;
  },
};

// Dashboard services
const dashboardService = {
  /**
   * Get all Facebook pages connected to the authenticated member's account
   * @returns {Promise} - Promise with list of pages
   */
  getPages: () => {
    return api.get(`/dashboard/pages`).then((response) => response.data);
  },

  /**
   * Get the currently active package information for the authenticated member
   * @returns {Promise} - Promise with active package details
   */
  getActivePackage: () => {
    return api.get(`/dashboard/active-package`).then((response) => response.data);
  },

  /**
   * Get upcoming advertisement tasks scheduled for the authenticated member
   * @param {Object} [params] - Optional query parameters
   * @param {number} [params.limit] - Maximum number of tasks to return (default: 5, max: 50)
   * @returns {Promise} - Promise with incoming tasks
   */
  getIncomingAdsTasks: (params = {}) => {
    return api.get(`/dashboard/ads-tasks/incoming`, { params }).then((response) => response.data);
  },

  /**
   * Get advertisement tasks within a specified date range
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date/time (ISO 8601)
   * @param {string} params.endDate - End date/time (ISO 8601)
   * @returns {Promise} - Promise with tasks in date range
   */
  getAdsTasksByDateRange: (params) => {
    return api.get(`/dashboard/ads-tasks/by-datetime`, { params }).then((response) => response.data);
  },

  /**
   * Get weekly summary of advertisement tasks for a specific month and year
   * @param {Object} params - Query parameters
   * @param {number} params.month - Month number (1-12)
   * @param {number} params.year - Year (e.g., 2025)
   * @returns {Promise} - Promise with weekly summary
   */
  getAdsTasksSummaryByWeek: (params) => {
    return api.get(`/dashboard/ads-tasks/summary-by-week`, { params }).then((response) => response.data);
  },
};

export {
  api,
  adminApi,
  authService,
  memberService,
  facebookPermissionService,
  facebookService,
  subMemberService,
  permissionService,
  adminAuthService,
  adminFacebookService,
  adsQueueService,
  packagesService,
  helpCenterService,
  adsStatusService,
  dashboardService,
};
